import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from '@app/constants/limits';
import { getDeliveryWindows } from '@app/services/deliveryWindowRepository';
import { getIsFeatureEnabled } from '@app/utils/getIsFeatureEnabled';
import { isDefined } from '@app/utils/is';
import { useInfiniteQuery } from '@tanstack/react-query';
import React from 'react';

import { DeliveryWindow } from '../../../../../typings';
import { useDebouncedCallback } from '../useDebouncedCallback';

const DEBOUNCE_TIME = 500;
const DELIVERY_WINDOWS_QUERY_KEY = 'delivery-windows';

interface Props {
  status?: DeliveryWindow.Status;
  sortBy?: DeliveryWindow.SortByDate;
  marketId?: DeliveryWindow.Id;
  sortOrder?: DeliveryWindow.SortOrder;
  isExpired?: boolean;
  isHidden?: boolean;
}

export const useDeliveryWindows = ({ status, marketId, sortBy = 'saleStartDate', sortOrder = 'asc' }: Props) => {
  const [search, setSearch] = React.useState('');
  const isInactiveDeliveryWindowsForLookbookEnabled = getIsFeatureEnabled('ENABLE_INACTIVE_DELIVERY_WINDONWS_FOR_LOOKBOOKS');

  const fetchDeliveryWindows = async ({ pageParam }: { pageParam: number }) => {
    const statusToUse = isInactiveDeliveryWindowsForLookbookEnabled ? status : 'active';

    return getDeliveryWindows({
      limit: DEFAULT_ITEMS_COUNT_PER_REQUEST,
      marketId,
      name: search,
      page: pageParam,
      sortBy,
      sortOrder,
      status: statusToUse,
    });
  };

  const { data, fetchNextPage, hasNextPage, isFetching } = useInfiniteQuery({
    gcTime: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (!isDefined(lastPage) || lastPage.length < DEFAULT_ITEMS_COUNT_PER_REQUEST) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    initialPageParam: 1,
    queryFn: fetchDeliveryWindows,
    queryKey: [DELIVERY_WINDOWS_QUERY_KEY, { marketId, search, status }],
  });

  const deliveryWindowsOptions = data?.pages.flatMap(page => page) ?? [];

  const handleSearchChange = useDebouncedCallback((value: string) => {
    setSearch(value);
  }, DEBOUNCE_TIME);

  const handleLoadMore = React.useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  return { deliveryWindowsOptions, fetchNextPage, handleLoadMore, handleSearchChange, hasNextPage, isFetching };
};
